import { Link } from 'react-router-dom';
import { ShoppingBag, Package, CreditCard, PhoneCall, ShieldCheck, ArrowRight } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { SEO } from '@/lib/seo';
import { Reveal } from '@/lib/motion';
import { BUSINESS } from '@/data/site';
import ZipChecker from '@/components/ZipChecker';

const STEPS = [
  { icon: ShoppingBag, n: '01', title: 'Browse the menu', text: 'Explore our live catalog — flower, pre-rolls, vapes, edibles, concentrates and more. Filter by category or strain type, or search for exactly what you want.' },
  { icon: Package, n: '02', title: 'Add to cart', text: `Quick-add products straight from the menu. Keep an eye on the $${BUSINESS.minOrder} minimum — the cart shows your progress the whole way.` },
  { icon: CreditCard, n: '03', title: 'Checkout', text: 'Choose pickup at our 7th Street shop or delivery to your door, and upload a photo of your ID.' },
  { icon: PhoneCall, n: '04', title: 'Verification', text: 'Our team verifies your ID and order details. This quick step keeps every order accurate and secure.' },
  { icon: ShieldCheck, n: '05', title: 'Pickup or delivery', text: 'Once verified, your order is ready for in-store pickup or delivered by our local Washington, DC team.' },
];

const FAQS = [
  { q: 'What is the minimum order?', a: `Our minimum order is $${BUSINESS.minOrder}. The cart shows a progress bar so you always know how close you are.` },
  { q: 'Why do I need to upload my ID?', a: 'All customers must be 21 or older. Uploading a photo of your valid ID at checkout lets our team verify your order quickly and keeps the process compliant and secure.' },
  { q: 'Who can order?', a: 'Adults 21 years of age and older with valid ID, located within our service area.' },
  { q: 'Which areas do you serve?', a: 'All of Washington, DC, plus parts of Maryland and Virginia. Use the checker below for an instant answer, or call us to confirm.' },
  { q: 'How do I know what is in stock?', a: 'Our online menu is live — it reflects the current store catalog, including availability and pricing.' },
];

export default function HowToOrder() {
  return (
    <>
      <SEO
        title="How to Order | Majestic Greens DMV — Washington, DC"
        description={`How ordering works at Majestic Greens DMV: browse the live menu, build your cart ($${BUSINESS.minOrder} minimum), checkout, quick verification call, fulfillment. Adults 21+.`}
        path="/how-to-order"
      />
      <div data-testid="how-to-order-page" className="mx-auto max-w-5xl px-6 pb-28 pt-28">
        <Reveal>
          <p className="text-[11px] font-bold uppercase tracking-[0.4em] text-bronze">The Process</p>
          <h1 className="mt-4 font-serif text-4xl text-ink sm:text-6xl">How to order</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-sage">
            Five simple steps from browsing to your door. No accounts required, no confusing steps — just a fast, clear process built around you.
          </p>
        </Reveal>

        <div className="mt-16 space-y-0">
          {STEPS.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.08}>
              <div className="group flex gap-8 border-b border-ink/10 py-10 transition-colors hover:border-gold/30">
                <span className="font-serif text-5xl text-forest transition-colors group-hover:text-bronze sm:text-6xl">{step.n}</span>
                <div>
                  <step.icon className="h-6 w-6 text-bronze" />
                  <h2 className="mt-3 font-serif text-2xl text-ink sm:text-3xl">{step.title}</h2>
                  <p className="mt-3 max-w-xl text-sm leading-relaxed text-sage">{step.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-16 border border-gold/30 bg-gold/5 p-8">
            <h2 className="font-serif text-2xl text-ink">First, confirm we serve you</h2>
            <div className="mt-6"><ZipChecker /></div>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <h2 className="mt-20 font-serif text-3xl text-ink sm:text-4xl">Common questions</h2>
          <Accordion type="single" collapsible className="mt-8">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-ink/10">
                <AccordionTrigger data-testid={`howto-faq-${i}`} className="text-left font-serif text-lg text-ink hover:text-bronze">{f.q}</AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-sage">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>

        <Reveal delay={0.15}>
          <Link
            to="/shop"
            data-testid="howto-shop-cta"
            className="group mt-16 inline-flex items-center gap-3 bg-gold px-10 py-5 text-xs font-bold uppercase tracking-[0.25em] text-ink transition-colors hover:bg-bronzehover"
          >
            Start Shopping <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </>
  );
}
